/**
 * Agent Sessions 훅 — 세션 목록/히스토리 관리
 *
 * - useAgentSessions(): 세션 목록 조회 (useQuery)
 * - useAgentMessages(): 특정 세션의 메시지 목록 (useQuery)
 * - useDeleteSession(): 세션 삭제 (useMutation)
 * - useArchiveSession(): 세션 아카이브 (useMutation)
 * - useUpdateSessionTitle(): 세션 제목 변경 (useMutation)
 *
 * layout-ux-builder ConversationHistory 컴포넌트에서 사용
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/shared/lib/supabase";
import { agentKeys } from "@/api/hooks/query-keys";
import type {
  AgentSession,
  ChatMessage,
  AgentWidget,
  Citation,
  ExecutionStep,
  ToolCall,
} from "@modootoday/core-agent/types";

/* ══════════════════════════════════════════════════════════════════════════
   DB 행 → 도메인 타입 변환
   ══════════════════════════════════════════════════════════════════════════ */

interface SessionRow {
  id: string;
  user_id: string;
  agent_id: string;
  title: string;
  metadata: Record<string, unknown> | null;
  message_count: number;
  last_message_at: string;
  status: string;
  created_at: string;
  updated_at: string;
}

interface MessageRow {
  id: string;
  session_id: string;
  role: string;
  content: string;
  widgets: AgentWidget[];
  citations: Citation[];
  suggestions: string[];
  steps: ExecutionStep[];
  tool_calls: ToolCall[];
  token_usage: { promptTokens: number; completionTokens: number } | null;
  created_at: string;
}

function rowToSession(row: SessionRow): AgentSession {
  return {
    id: row.id,
    userId: row.user_id,
    agentId: row.agent_id,
    title: row.title,
    metadata: row.metadata ?? undefined,
    messageCount: row.message_count,
    lastMessageAt: row.last_message_at,
    status: row.status as AgentSession["status"],
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function rowToMessage(row: MessageRow): ChatMessage {
  const timestamp = new Date(row.created_at).getTime();

  if (row.role === "user") {
    return {
      role: "user",
      id: row.id,
      content: row.content,
      timestamp,
    };
  }

  const widgets = row.widgets ?? [];
  const content = row.content ?? "";
  const steps = row.steps ?? [];

  // DB 로드 시 segments 재구성 (historical backward compat):
  // - 정확한 이벤트 순서는 DB에 저장되지 않으므로 간소화
  // - 순서: done된 step들 → 전체 텍스트 → 모든 위젯
  // 스트리밍 메시지는 use-agent-chat이 직접 segments를 유지하므로 여기만 처리
  const segments: import("@modootoday/core-agent/types").MessageSegment[] = [];
  for (const step of steps) {
    if (step.status === "done" || step.status === "error") {
      segments.push({ kind: "step", step });
    }
  }
  if (content.length > 0) {
    segments.push({ kind: "text", content });
  }
  widgets.forEach((widget, widgetIndex) => {
    segments.push({ kind: "widget", widget, widgetIndex });
  });

  return {
    role: "assistant",
    id: row.id,
    content,
    widgets,
    citations: row.citations ?? [],
    suggestions: row.suggestions ?? [],
    steps,
    segments,
    isStreaming: false,
    timestamp,
    usage: row.token_usage ?? undefined,
  };
}

/* ══════════════════════════════════════════════════════════════════════════
   useAgentSessions — 세션 목록
   ══════════════════════════════════════════════════════════════════════════ */

export function useAgentSessions(agentId?: string) {
  return useQuery({
    queryKey: agentKeys.sessions(),
    queryFn: async (): Promise<AgentSession[]> => {
      let query = supabase
        .from("agent_sessions")
        .select(
          "id,user_id,agent_id,title,message_count,last_message_at,status,created_at,updated_at",
        )
        .neq("status", "deleted")
        .order("last_message_at", { ascending: false })
        .limit(50);

      if (agentId) {
        query = query.eq("agent_id", agentId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return ((data as SessionRow[]) ?? []).map(rowToSession);
    },
    staleTime: 30_000,
  });
}

/* ══════════════════════════════════════════════════════════════════════════
   useAgentSession — 단일 세션
   ══════════════════════════════════════════════════════════════════════════ */

export function useAgentSession(sessionId: string | null) {
  return useQuery({
    queryKey: agentKeys.session(sessionId ?? ""),
    queryFn: async (): Promise<AgentSession | null> => {
      if (!sessionId) return null;

      const { data, error } = await supabase
        .from("agent_sessions")
        .select(
          "id,user_id,agent_id,title,message_count,last_message_at,status,created_at,updated_at",
        )
        .eq("id", sessionId)
        .single();

      if (error) throw error;
      if (!data) return null;
      return rowToSession(data as SessionRow);
    },
    enabled: !!sessionId,
    staleTime: 30_000,
  });
}

/* ══════════════════════════════════════════════════════════════════════════
   useAgentMessages — 세션 메시지 목록
   ══════════════════════════════════════════════════════════════════════════ */

export function useAgentMessages(sessionId: string | null) {
  return useQuery({
    queryKey: agentKeys.messages(sessionId ?? ""),
    queryFn: async (): Promise<ChatMessage[]> => {
      if (!sessionId) return [];

      const { data, error } = await supabase
        .from("agent_messages")
        .select(
          "id,session_id,role,content,widgets,citations,suggestions,steps,tool_calls,token_usage,created_at",
        )
        .eq("session_id", sessionId)
        .order("created_at", { ascending: true });

      if (error) throw error;
      return ((data as MessageRow[]) ?? []).map(rowToMessage);
    },
    enabled: !!sessionId,
    staleTime: 10_000,
  });
}

/* ══════════════════════════════════════════════════════════════════════════
   createAgentSession — 서버 사전 세션 생성

   /chat → /chat/:sessionId 이동 전에 서버에서 빈 세션을 미리 만들고 ID를
   수신한다. 클라이언트 UUID 생성은 collision 시도로 악용될 수 있으므로
   session ID 결정권은 항상 서버에 둔다. 백엔드: agent-chat EF 의
   POST /functions/v1/agent-chat/session 엔드포인트.
   ══════════════════════════════════════════════════════════════════════════ */

export async function createAgentSession(agentId: string): Promise<string> {
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
  if (!supabaseUrl) throw new Error("VITE_SUPABASE_URL is not set");

  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData.session?.access_token;
  if (!token) throw new Error("로그인이 필요합니다.");

  const resp = await fetch(`${supabaseUrl}/functions/v1/agent-chat/session`, {
    method: "POST",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ agentId }),
  });

  if (!resp.ok) {
    const errBody = await resp
      .json()
      .catch(() => ({}) as Record<string, unknown>);
    throw new Error(
      typeof errBody.error === "string"
        ? errBody.error
        : "세션 생성에 실패했습니다.",
    );
  }

  const body = (await resp.json()) as { sessionId?: string };
  if (!body.sessionId) throw new Error("서버 응답에 sessionId가 없습니다.");
  return body.sessionId;
}

/* ══════════════════════════════════════════════════════════════════════════
   useDeleteSession
   ══════════════════════════════════════════════════════════════════════════ */

export function useDeleteSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (sessionId: string) => {
      const { error } = await supabase
        .from("agent_sessions")
        .update({ status: "deleted", updated_at: new Date().toISOString() })
        .eq("id", sessionId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: agentKeys.sessions() });
    },
  });
}

/* ══════════════════════════════════════════════════════════════════════════
   useArchiveSession
   ══════════════════════════════════════════════════════════════════════════ */

export function useArchiveSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (sessionId: string) => {
      const { error } = await supabase
        .from("agent_sessions")
        .update({ status: "archived", updated_at: new Date().toISOString() })
        .eq("id", sessionId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: agentKeys.sessions() });
    },
  });
}

/* ══════════════════════════════════════════════════════════════════════════
   useUpdateSessionTitle
   ══════════════════════════════════════════════════════════════════════════ */

export function useUpdateSessionTitle() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      sessionId,
      title,
    }: {
      sessionId: string;
      title: string;
    }) => {
      const { error } = await supabase
        .from("agent_sessions")
        .update({ title, updated_at: new Date().toISOString() })
        .eq("id", sessionId);
      if (error) throw error;
    },
    onSuccess: (_data, { sessionId }) => {
      queryClient.invalidateQueries({ queryKey: agentKeys.sessions() });
      queryClient.invalidateQueries({ queryKey: agentKeys.session(sessionId) });
    },
  });
}
